import { ActionIcon, Select, useMantineTheme } from '@mantine/core'
import { CSSProperties, useMemo } from 'react'
import { TbChevronDown, TbChevronUp, TbSelector } from 'react-icons/tb'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TDatatable } from '.'
import { DatatableRow } from './DatatableRow'
import classes from './Datatable.module.css'

export type DatatableProps<TItem> = {
  headers: TDatatable<TItem>[]
  exclude?: string[]
  noShadow?: boolean
  fixedLayout?: boolean
  data: TItem[]
  href?: (_data: TItem) => string
  onClick?: (_data: TItem) => void
}

export const Datatable = <TItem,>({
  exclude = [],
  headers,
  noShadow = false,
  fixedLayout = false,
  data,
  href,
  onClick,
}: DatatableProps<TItem>) => {
  const width = headers.reduce((acc, { colSize }) => acc + colSize, 0)
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const wrapperStyle = {
    '--datatable-wrapper-background': 'var(--cl-app-surface-elevated)',
    '--datatable-wrapper-border-color': 'var(--cl-app-border)',
    '--datatable-wrapper-radius': theme.radius.md,
    '--datatable-wrapper-shadow': 'var(--cl-app-shadow)',
    '--datatable-table-radius': theme.radius.md,
    '--datatable-header-background': 'var(--cl-app-surface-subtle)',
    '--datatable-header-border-color': 'var(--cl-app-border)',
    '--datatable-header-color': 'var(--cl-app-text-muted)',
  } as CSSProperties

  const filteredHeaders = useMemo(() => {
    if (exclude.length === 0) return headers

    return headers.filter(header => !exclude.includes(header.id))
  }, [exclude, headers])

  const sortableHeaders = filteredHeaders.filter(header => header.isSortable)
  const currentSortKey = searchParams.get('orderBy')
  const currentSortOrder =
    searchParams.get('sortOrder') === 'DESC' ? 'DESC' : 'ASC'

  const getSortKey = (header: TDatatable<TItem>) => header.sortKey ?? header.id

  const setSort = (sortKey: string, sortOrder: 'ASC' | 'DESC') => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('orderBy', sortKey)
    nextParams.set('sortOrder', sortOrder)
    nextParams.set('page', '1')
    setSearchParams(nextParams)
  }

  const resetSort = () => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('orderBy')
    nextParams.delete('sortOrder')
    nextParams.set('page', '1')
    setSearchParams(nextParams)
  }

  const handleSort = (header: TDatatable<TItem>) => {
    const sortKey = getSortKey(header)

    if (currentSortKey !== sortKey) {
      setSort(sortKey, 'ASC')
      return
    }

    if (currentSortOrder === 'ASC') {
      setSort(sortKey, 'DESC')
      return
    }

    resetSort()
  }

  const handleMobileSortChange = (sortKey: string | null) => {
    if (sortKey) {
      setSort(sortKey, 'ASC')
    } else {
      resetSort()
    }
  }

  const toggleMobileSortOrder = () => {
    if (!currentSortKey) return
    setSort(currentSortKey, currentSortOrder === 'ASC' ? 'DESC' : 'ASC')
  }

  return (
    <div
      className={`${classes.wrapper} ${noShadow ? '' : classes.wrapperShadow}`}
      style={wrapperStyle}
    >
      {sortableHeaders.length > 0 && (
        <div className={classes.mobileSort}>
          <Select
            aria-label={t('generic.sortBy')}
            data={sortableHeaders.map(header => ({
              value: getSortKey(header),
              label:
                typeof header.label === 'string' ? header.label : header.id,
            }))}
            clearable
            placeholder={t('generic.sortBy')}
            value={
              sortableHeaders.some(
                header => getSortKey(header) === currentSortKey,
              )
                ? currentSortKey
                : null
            }
            onChange={handleMobileSortChange}
            size="sm"
          />
          <ActionIcon
            aria-label={
              currentSortOrder === 'ASC'
                ? t('generic.descending')
                : t('generic.ascending')
            }
            disabled={!currentSortKey}
            onClick={toggleMobileSortOrder}
            size="lg"
            variant="light"
          >
            {currentSortKey ? (
              currentSortOrder === 'ASC' ? (
                <TbChevronUp size="1.2rem" />
              ) : (
                <TbChevronDown size="1.2rem" />
              )
            ) : (
              <TbSelector size="1.2rem" />
            )}
          </ActionIcon>
        </div>
      )}
      <table
        className={classes.table}
        style={{ tableLayout: fixedLayout ? 'fixed' : undefined }}
      >
        <thead className={classes.thead}>
          <tr className={classes.tr}>
            {filteredHeaders.map(header => {
              const { id, label, colSize, isSortable } = header
              const sortKey = getSortKey(header)
              const isSorted = currentSortKey === sortKey

              return (
                <th
                  className={classes.th}
                  aria-sort={
                    isSortable && isSorted
                      ? currentSortOrder === 'ASC'
                        ? 'ascending'
                        : 'descending'
                      : isSortable
                      ? 'none'
                      : undefined
                  }
                  style={{
                    width: `${Math.round((colSize / width) * 10000) / 100}%`,
                  }}
                  key={id}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      className={classes.sortButton}
                      onClick={() => handleSort(header)}
                    >
                      <span>{label}</span>
                      {isSorted ? (
                        currentSortOrder === 'ASC' ? (
                          <TbChevronUp aria-hidden size="1.1rem" />
                        ) : (
                          <TbChevronDown aria-hidden size="1.1rem" />
                        )
                      ) : (
                        <TbSelector aria-hidden size="1.1rem" />
                      )}
                    </button>
                  ) : (
                    label
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className={classes.tbody}>
          {data.map((item, i) => (
            <DatatableRow
              href={href}
              onClick={onClick}
              key={i}
              value={item}
              headers={filteredHeaders}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
